import React, { useState, useEffect } from "react";
import { formatDate } from "@/lib/dateUtils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  CheckCircle,
  AlertTriangle,
  DollarSign,
  CreditCard,
  Banknote,
  FileSpreadsheet,
  Fuel,
  Droplet,
  LineChart,
  Wallet,
  Coins,
  Calculator,
  Plus,
  X
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { daySettlementSchema } from "@/lib/validations";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import { fetchJSON } from "@/lib/api";

type DaySettlementForm = z.infer<typeof daySettlementSchema>;

const cleanNotes = (notes: string | null | undefined): string => {
  if (!notes) return "";
  return notes.replace(/\[DaySettlementDetails:\{[\s\S]*?\}\]|\[Petty Cash:.*?\]|\[Cash Inflow:.*?\]|\[Flow:.*?\]/g, "").trim();
};

const exportToCSV = (data: any[]) => {
  const headers = ["S.No", "Date", "Opening Balance", "Meter Sale", "Lubricant Sale", "Credit Amount", "Total Sale", "Expenses", "Shortage", "Closing Balance", "Notes"];
  const csvContent = "data:text/csv;charset=utf-8,"
    + headers.join(",") + "\n"
    + data.map((row, index) => {
      return [
        index + 1,
        formatDate(row.settlement_date),
        row.opening_balance,
        row.meter_sale,
        row.lubricant_sale,
        row.credit_amount,
        row.total_sale,
        row.expenses,
        row.shortage,
        row.closing_balance,
        `"${cleanNotes(row.notes)}"`
      ].join(",");
    }).join("\n");

  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", "day_settlements.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const mapProductName = (name: string) => {
  const lower = name.toLowerCase();
  if (lower.includes("motor spirit") || lower === "ms") return "Motor Spirit";
  if (lower.includes("high speed") || lower.includes("hsd") || lower.includes("diesel")) return "High Speed Desi";
  if (lower.includes("extra premium") || lower.includes("xtra premium") || lower.includes("ep") || lower.includes("xp")) return "Extra Premium";
  return name;
};

const mapSwipeMode = (mode: string) => {
  const m = mode.trim();
  const ml = m.toLowerCase();
  if (ml === "phonepe" || ml === "phone pe") return "phone pe";
  if (ml === "ccms") return "CCMS";
  if (ml === "icici card" || ml === "icici_card") return "ICICI CARD";
  if (ml === "icici scan" || ml === "icici_scan") return "ICICI SCAN";
  if (ml === "account pay" || ml === "account_pay") return "account pay";
  return m;
};

export default function Settlement() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState<string>(""); // empty initially to show "Date" button placeholder
  const [editingId, setEditingId] = useState<string | null>(null);

  // New states to match screenshots
  const [pettyCash, setPettyCash] = useState<number>(0);
  const [anyCashInflow, setAnyCashInflow] = useState<boolean>(false);
  const [cashInflowAmount, setCashInflowAmount] = useState<number>(0);
  const [manualTransactionBalance, setManualTransactionBalance] = useState<number | null>(null);
  const [balanceCashFlow, setBalanceCashFlow] = useState<"In" | "Out">("In"); // default to In
  const [sendWhatsApp, setSendWhatsApp] = useState<boolean>(false);

  // Dynamic arrays for OUT cash flow section
  const [cashOutTransactions, setCashOutTransactions] = useState<Array<{
    id: string;
    type: string;
    handedTo: string;
    amount: number;
    note: string;
  }>>([{ id: "1", type: "", handedTo: "", amount: 0, note: "" }]);

  const [vendorPayments, setVendorPayments] = useState<Array<{
    id: string;
    vendorId: string;
    paidAmount: number;
    note: string;
  }>>([{ id: "1", vendorId: "", paidAmount: 0, note: "" }]);

  // Summary Panel Modal State
  const [isSummaryOpen, setIsSummaryOpen] = useState<boolean>(false);
  const [pendingData, setPendingData] = useState<DaySettlementForm | null>(null);
  const [isClosingBalanceManual, setIsClosingBalanceManual] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<DaySettlementForm>({
    resolver: zodResolver(daySettlementSchema),
    defaultValues: {
      opening_balance: 0,
      meter_sale: 0,
      lubricant_sale: 0,
      credit_amount: 0,
      total_sale: 0,
      expenses: 0,
      cash_to_bank: 0,
      closing_balance: "" as any,  // empty string avoids NaN display; preprocess will handle conversion
      shortage: 0,
      notes: "",
    }
  });

  const closingBalanceField = register("closing_balance", {
    onChange: () => setIsClosingBalanceManual(true),
  });

  // Fetch vendors
  const { data: vendorsList } = useQuery({
    queryKey: ["/api/vendors"],
    queryFn: async () => {
      const response = await fetchJSON("/api/vendors");
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || "Failed to fetch vendors");
      return result.rows || [];
    }
  });

  // Fetch sales data for selected date
  const { data: salesData, isLoading: salesLoading, error: salesError, refetch: refetchSales } = useQuery({
    queryKey: ["/api/settlement-sales", selectedDate],
    queryFn: async () => {
      if (!selectedDate) return null;
      const isSameDate = (d: any, t: string) => {
        if (!d) return false;
        const dStr = typeof d === 'string' ? d : new Date(d).toISOString();
        return dStr.startsWith(t);
      };
      const getAmt = (obj: any) =>
        Number(obj.total_amount || obj.totalAmount || obj.net_sale_amount || obj.amount || 0);

      const loadRows = async (path: string) => {
        const response = await fetchJSON(path);
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result?.ok) return [] as any[];
        return result.rows || [];
      };

      const [guestRows, swipeRows, creditRows, lubRows, meterRows] = await Promise.all([
        loadRows('/api/guest-sales'),
        loadRows('/api/swipe-transactions'),
        loadRows('/api/credit-sales'),
        loadRows('/api/lubricant-sales'),
        loadRows(`/api/sale-entries?from=${selectedDate}&to=${selectedDate}`),
      ]);

      const guestSales = guestRows.filter((s: any) => isSameDate(s.sale_date || s.saleDate, selectedDate));
      const swipeSales = swipeRows.filter((s: any) => isSameDate(s.transaction_date || s.transactionDate, selectedDate));
      const creditSales = creditRows.filter((s: any) => isSameDate(s.sale_date || s.saleDate, selectedDate));
      const lubricantSales = lubRows.filter((s: any) => isSameDate(s.sale_date || s.saleDate, selectedDate));

      const cashSales = guestSales
        .filter((s: any) => (s.payment_mode || s.paymentMode) === "Cash")
        .reduce((sum: number, s: any) => sum + getAmt(s), 0);
      const cardSales = guestSales
        .filter((s: any) => (s.payment_mode || s.paymentMode) === "Card")
        .reduce((sum: number, s: any) => sum + getAmt(s), 0);
      const upiSales = guestSales
        .filter((s: any) => (s.payment_mode || s.paymentMode) === "UPI")
        .reduce((sum: number, s: any) => sum + getAmt(s), 0);
      const swipeTotal = swipeSales.reduce((sum: number, s: any) => sum + getAmt(s), 0);
      // Net credit = total_amount - advance (advance already paid in cash so not a non-cash deduction)
      const creditTotal = creditSales.reduce((sum: number, s: any) => {
        const total = Number(s.total_amount || s.totalAmount || 0);
        const advance = Number(s.advance || 0);
        return sum + Math.max(0, total - advance);
      }, 0);
      const lubricantTotal = lubricantSales.reduce((sum: number, s: any) => sum + getAmt(s), 0);
      const meterTotal = meterRows.reduce((sum: number, s: any) => sum + getAmt(s), 0);

      // Total Sale = Meter Sale + Lubricant Sale only.
      // Credit and Swipe are NON-CASH portions already included inside Meter Sale —
      // they must NOT be added again. They are only deducted from cash-in-hand calculation.
      const totalSales = meterTotal + lubricantTotal;

      return {
        cashSales,
        cardSales,
        upiSales,
        swipeSales: swipeTotal,   // non-cash deduction from cash-in-hand
        creditSales: creditTotal, // non-cash deduction from cash-in-hand
        lubricantSales: lubricantTotal,
        meterSales: meterTotal,
        totalSales,
        _rawSwipeSales: swipeSales,
        _rawMeterSales: meterRows,
      };
    },
    enabled: !!selectedDate
  });

  // Fetch expenses for selected date
  const { data: expensesData, isLoading: expensesLoading, error: expensesError } = useQuery({
    queryKey: ["/api/settlement-expenses", selectedDate],
    queryFn: async () => {
      if (!selectedDate) return 0;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetchJSON('/api/expenses', { signal: controller.signal });
        const result = await response.json();

        if (!result.ok) throw new Error(result.error);

        const isSameDate = (d: any, t: string) => {
          if (!d) return false;
          const dStr = typeof d === 'string' ? d : new Date(d).toISOString();
          return dStr.startsWith(t);
        };

        const filteredExpenses = (result.rows || [])
          .filter((e: any) => isSameDate(e.expense_date || e.expenseDate, selectedDate));

        return filteredExpenses.reduce((sum: number, e: any) => sum + (e.amount || 0), 0) || 0;
      } finally { clearTimeout(timeoutId); }
    },
    enabled: !!selectedDate
  });

  // Fetch recoveries for selected date (updated to return mode breakdown)
  const { data: recoveriesData } = useQuery({
    queryKey: ["/api/settlement-recoveries", selectedDate],
    queryFn: async () => {
      if (!selectedDate) return { CASH: 0, BANK: 0, SWIPE: 0, IOCL: 0, TOTAL: 0 };
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetchJSON(`/api/recoveries?date=${selectedDate}`, { signal: controller.signal });
        const result = await response.json();
        if (!result.ok) throw new Error(result.error);

        const rows = result.rows || [];
        const breakdown = {
          CASH: 0,
          BANK: 0,
          SWIPE: 0,
          IOCL: 0,
          TOTAL: 0
        };
        for (const r of rows) {
          const mode = String(r.payment_mode || r.paymentMode || "").trim().toLowerCase();
          const amt = Number(r.received_amount || r.receivedAmount || 0);
          breakdown.TOTAL += amt;
          if (mode === "cash") {
            breakdown.CASH += amt;
          } else if (mode === "bank" || mode === "bank transfer" || mode === "cheque" || mode === "upi") {
            breakdown.BANK += amt;
          } else if (mode === "swipe" || mode === "card") {
            breakdown.SWIPE += amt;
          } else if (mode === "iocl") {
            breakdown.IOCL += amt;
          } else {
            breakdown.BANK += amt;
          }
        }
        return breakdown;
      } finally { clearTimeout(timeoutId); }
    },
    enabled: !!selectedDate
  });

  // Employee Cash Recovery is the actual cash received from staff. It is the
  // authoritative closing-cash source when recovery records exist for a date.
  const { data: employeeCashSummary } = useQuery({
    queryKey: ["/api/settlement-employee-cash", selectedDate],
    queryFn: async () => {
      if (!selectedDate) return { hasRecords: false, collectedCash: 0, recordedShortage: 0 };
      const response = await fetchJSON(`/api/employee-cash-recovery?from_date=${selectedDate}&to_date=${selectedDate}`);
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || "Failed to fetch employee cash collections");

      const rows = result.rows || [];
      return {
        hasRecords: rows.length > 0,
        collectedCash: rows.reduce((sum: number, row: any) => sum + (Number(row.collection_amount) || 0), 0),
        recordedShortage: rows.reduce((sum: number, row: any) => sum + (Number(row.shortage_amount) || 0), 0),
      };
    },
    enabled: !!selectedDate,
  });

  const closingBalance = watch("closing_balance");
  const closingBalanceNum = Number(closingBalance) || 0;
  const openingBalance = Number(watch("opening_balance")) || 0;

  // Expected Cash = Day Opening Cash + Petty Cash + SALE AMOUNT + CASH RECOVERY + CASH INFLOW - SWIPES - CREDITS - EXPENSES - OUTFLOWS
  const swipeAmount = salesData?.swipeSales || 0;
  const creditAmount = salesData?.creditSales || 0;
  const cashRecovery = recoveriesData?.CASH || 0;

  // Outflow sums from dynamic lists
  const totalCashOutTransactions = balanceCashFlow === "Out" 
    ? cashOutTransactions.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
    : 0;

  const totalVendorPayments = balanceCashFlow === "Out"
    ? vendorPayments.reduce((sum, item) => sum + (Number(item.paidAmount) || 0), 0)
    : 0;

  const totalOutSectionAmount = totalCashOutTransactions + totalVendorPayments;

  // Net transaction balance for the day (excluding opening cash, petty, and outflows)
  const businessTransactionBalance = (salesData?.totalSales || 0) + cashRecovery - swipeAmount - creditAmount - (expensesData || 0);

  const transactionBalance = manualTransactionBalance !== null ? manualTransactionBalance : businessTransactionBalance;

  // Total expected cash including opening cash, petty cash, inflow, minus outflows
  const expectedCash = openingBalance + pettyCash + transactionBalance + cashInflowAmount - totalOutSectionAmount;

  const hasClosingBalance = closingBalance !== undefined && closingBalance !== null && closingBalance !== "";
  // A positive value consistently means cash is short, matching Employee Cash Recovery.
  const difference = hasClosingBalance ? expectedCash - closingBalanceNum : 0;

  // Employee collections cover the day's business cash. Opening cash and explicit
  // settlement inflows/outflows remain outside the employee collection process.
  const suggestedClosingBalance = (employeeCashSummary?.hasRecords
    ? employeeCashSummary.collectedCash
    : transactionBalance) + openingBalance + pettyCash + cashInflowAmount - totalOutSectionAmount;

  // Auto-populate new settlements from cash actually collected from employees.
  // Operators may still overwrite the amount after physically counting the cash.
  useEffect(() => {
    if (!editingId && selectedDate && !isClosingBalanceManual) {
      setValue("closing_balance", suggestedClosingBalance as any);
    }
  }, [suggestedClosingBalance, selectedDate, editingId, isClosingBalanceManual, setValue]);

  const formatInr = (n: number) =>
    Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Add/Remove handlers for dynamic arrays
  const addCashOutRow = () => {
    setCashOutTransactions([
      ...cashOutTransactions,
      { id: Date.now().toString(), type: "", handedTo: "", amount: 0, note: "" }
    ]);
  };

  const removeCashOutRow = (id: string) => {
    setCashOutTransactions(cashOutTransactions.filter(item => item.id !== id));
  };

  const updateCashOutRow = (id: string, field: string, value: any) => {
    setCashOutTransactions(cashOutTransactions.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  const addVendorPaymentRow = () => {
    setVendorPayments([
      ...vendorPayments,
      { id: Date.now().toString(), vendorId: "", paidAmount: 0, note: "" }
    ]);
  };

  const removeVendorPaymentRow = (id: string) => {
    setVendorPayments(vendorPayments.filter(item => item.id !== id));
  };

  const updateVendorPaymentRow = (id: string, field: string, value: any) => {
    setVendorPayments(vendorPayments.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  // Create or update settlement mutation
  const saveSettlement = useMutation({
    mutationFn: async (formData: DaySettlementForm) => {
      const settlementData = {
        settlement_date: selectedDate,
        opening_balance: Number(formData.opening_balance) || 0,
        meter_sale: salesData?.meterSales ?? salesData?.totalSales ?? 0,
        lubricant_sale: salesData?.lubricantSales || 0,
        credit_amount: salesData?.creditSales || 0,
        swipe_amount: salesData?.swipeSales || 0,
        total_sale: salesData?.totalSales || 0,
        expenses: expensesData || 0,
        cash_to_bank: 0, 
        closing_balance: Number(formData.closing_balance) || 0,
        shortage: expectedCash - (Number(formData.closing_balance) || 0),
        notes: formData.notes || null,
        created_by: null,
      };

      const isEdit = Boolean(editingId);
      const url = isEdit ? `/api/day-settlements/${editingId}` : '/api/day-settlements';
      const method = isEdit ? 'PUT' : 'POST';
      const response = await fetchJSON(url, {
        method,
        body: JSON.stringify(settlementData)
      });
      const result = await response.json();

      if (!result.ok) throw new Error(result.error || 'Failed to save settlement');
    },
    onSuccess: () => {
      toast({
        title: "Saved",
        description: "Settlement saved successfully",
      });
      reset();
      setEditingId(null);
      setSelectedDate("");
      setIsClosingBalanceManual(false);
      setPettyCash(0);
      setAnyCashInflow(false);
      setCashInflowAmount(0);
      setBalanceCashFlow("In");
      setSendWhatsApp(false);
      setCashOutTransactions([{ id: "1", type: "", handedTo: "", amount: 0, note: "" }]);
      setVendorPayments([{ id: "1", vendorId: "", paidAmount: 0, note: "" }]);
      setManualTransactionBalance(null);
      // Refresh recent settlements list immediately
      queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey?.[0] || "").includes("/api/day-settlements") });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const { data: recentSettlements, isLoading: settlementsLoading, refetch: refetchSettlements } = useQuery({
    queryKey: ["/api/day-settlements"],
    queryFn: async () => {
      const response = await fetchJSON('/api/day-settlements');
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || 'Failed to fetch settlements');
      return (result.rows || []).slice(0, 10);
    },
  });

  const onSubmit = (data: DaySettlementForm) => {
    if (!selectedDate) {
      toast({
        title: "Date Required",
        description: "Please choose a date before confirming.",
        variant: "destructive",
      });
      return;
    }
    // Intercept to show the summary panel first
    setPendingData(data);
    setIsSummaryOpen(true);
  };

  const handleModalConfirm = () => {
    if (pendingData) {
      // Package extra form fields in notes for storage
      const details = {
        pettyCash,
        cashInflowAmount,
        balanceCashFlow,
        cashOutTransactions: balanceCashFlow === "Out" ? cashOutTransactions : [],
        vendorPayments: balanceCashFlow === "Out" ? vendorPayments : [],
        manualTransactionBalance
      };
      const extraMetadata = `[DaySettlementDetails:${JSON.stringify(details)}]`;
      const finalNotes = pendingData.notes ? `${pendingData.notes} ${extraMetadata}` : extraMetadata;

      saveSettlement.mutate({
        ...pendingData,
        notes: finalNotes
      });
      setIsSummaryOpen(false);
      setPendingData(null);
    }
  };

  const handleModalClose = () => {
    setIsSummaryOpen(false);
    setPendingData(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCSV = () => {
    if (recentSettlements && recentSettlements.length > 0) {
      exportToCSV(recentSettlements);
    } else {
      toast({ title: "No data", description: "No settlements to export" });
    }
  };

  // Group nozzle sales for the Product Table
  const productGroup: Record<string, { qty: number; value: number }> = {};
  if (salesData && salesData._rawMeterSales) {
    for (const r of salesData._rawMeterSales) {
      const pName = r.product_name || "Unknown Product";
      if (!productGroup[pName]) {
        productGroup[pName] = { qty: 0, value: 0 };
      }
      productGroup[pName].qty += Number(r.quantity || 0);
      productGroup[pName].value += Number(r.net_sale_amount || r.netSaleAmount || 0);
    }
  }

  const navItems = [
    { label: "See Transactions", path: "/day-cash-report", icon: FileSpreadsheet, color: "text-blue-500" },
    { label: "Sale", path: "/day-business/sale-entry", icon: Fuel, color: "text-red-500" },
    { label: "Lub Sale", path: "/day-business/lubricants", icon: Droplet, color: "text-amber-600" },
    { label: "Swipe", path: "/day-business/swipe", icon: CreditCard, color: "text-blue-600" },
    { label: "Credit", path: "/day-business/credit-sale", icon: LineChart, color: "text-emerald-500" },
    { label: "Expenses", path: "/day-business/expenses", icon: Wallet, color: "text-amber-800" },
    { label: "Recovery", path: "/day-business/recovery", icon: Coins, color: "text-indigo-500" },
    { label: "Day Settlement", path: "/day-business/day-settlement", icon: Calculator, color: "text-emerald-600" },
  ];

  return (
    <div className="p-6 space-y-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span className="font-semibold text-gray-700">Dashboard</span>
        <span>&gt;</span>
        <span>Add Day Settlement</span>
      </div>

      {/* Navigation Icons Sub-Bar */}
      <div className="flex flex-wrap items-center gap-2 bg-white p-2.5 rounded-xl border border-gray-150 shadow-sm mb-4">
        {navItems.map((item) => {
          const isActive = item.path === "/day-business/day-settlement";
          return (
            <a
              key={item.label}
              href={item.path}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${
                isActive
                  ? "bg-white border border-[#f37043] text-gray-900 shadow-sm"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <item.icon className={`h-5 w-5 ${isActive ? "text-orange-500" : item.color}`} />
              <span>{item.label}</span>
            </a>
          );
        })}
      </div>

      {/* Main Entry Panel */}
      <Card className="border-t-4 border-t-blue-600 shadow-md">
        <CardHeader className="bg-blue-600 text-white py-3">
          <CardTitle className="text-lg font-medium">Settlement</CardTitle>
        </CardHeader>
        <CardContent className="p-6 bg-blue-50 space-y-6">
          {(salesError || expensesError) && (
            <div className="p-3 rounded bg-red-600 text-white text-sm">
              <strong>Error:</strong> {String((salesError || expensesError) as any)}
            </div>
          )}
          {(salesLoading || expensesLoading) && (
            <div className="text-sm text-blue-600">Loading sales/expenses...</div>
          )}

          {/* Date Picker Row */}
          <div className="flex items-center gap-2 bg-white p-3 rounded-lg border-2 border-blue-200 w-fit">
            <span className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-l text-sm select-none">ChooseDate</span>
            <button
              type="button"
              className="h-10 px-4 bg-white text-gray-800 font-semibold hover:bg-gray-50 border border-blue-200 border-l-0 flex items-center justify-center min-w-44 text-sm"
              onClick={() => (document.getElementById('settlement_date_hidden') as HTMLInputElement | null)?.showPicker?.()}
            >
              {selectedDate || 'Date'}
            </button>
            <input
              id="settlement_date_hidden"
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setIsClosingBalanceManual(false);
              }}
              className="hidden"
            />
            <Button
              type="button"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-r text-sm h-10 px-4 font-bold"
              onClick={() => {
                refetchSales();
                queryClient.invalidateQueries({ queryKey: ["/api/settlement-expenses", selectedDate] });
                queryClient.invalidateQueries({ queryKey: ["/api/settlement-recoveries", selectedDate] });
              }}
            >
              OK
            </Button>
          </div>

          {/* Entry Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="grid grid-cols-12 gap-4">
              
              {/* Day Settlement bal. Cash (Inhand) */}
              <div className="col-span-12 md:col-span-3">
                <label className="text-blue-900 font-semibold block mb-1 text-sm">Day Settelment bal. Cash (Inhand)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={manualTransactionBalance !== null ? manualTransactionBalance : (selectedDate && businessTransactionBalance ? Number(businessTransactionBalance.toFixed(2)) : "")}
                  onChange={(e) => {
                    const val = e.target.value === "" ? null : Number(e.target.value);
                    setManualTransactionBalance(val);
                  }}
                  placeholder="Busi. Transaction balance"
                  className="bg-white text-gray-900 h-10 border-2 border-blue-200 focus:border-blue-500 rounded font-semibold text-sm"
                />
              </div>

              {/* Day Opening Cash */}
              <div className="col-span-12 md:col-span-3">
                <label className="text-blue-900 font-semibold block mb-1 text-sm">Day Opening Cash(Inhand)</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0"
                  className="bg-white text-gray-900 h-10 border-2 border-blue-200 focus:border-blue-500 rounded font-semibold text-sm"
                  {...register("opening_balance")}
                />
                {errors.opening_balance && (<p className="text-xs text-red-600 mt-1">{errors.opening_balance.message}</p>)}
              </div>

              {/* Petty Cash */}
              <div className="col-span-12 md:col-span-3">
                <label className="text-blue-900 font-semibold block mb-1 text-sm">Petty Cash</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Day petty cash"
                  value={pettyCash || ""}
                  onChange={(e) => setPettyCash(Number(e.target.value) || 0)}
                  className="bg-white text-gray-900 h-10 border-2 border-blue-200 focus:border-blue-500 rounded font-semibold text-sm"
                />
              </div>

              {/* Any Cash Inflow */}
              <div className="col-span-12 md:col-span-3 flex flex-col justify-start">
                <label className="text-blue-900 font-semibold flex items-center gap-2 h-10 select-none cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={anyCashInflow}
                    onChange={(e) => {
                      setAnyCashInflow(e.target.checked);
                      if (!e.target.checked) setCashInflowAmount(0);
                    }}
                    className="h-5 w-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>Any Cash Inflow ?</span>
                </label>
                {anyCashInflow && (
                  <div className="mt-1">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Enter Inflow Amount"
                      value={cashInflowAmount || ""}
                      onChange={(e) => setCashInflowAmount(Number(e.target.value) || 0)}
                      className="bg-white text-gray-900 h-10 border-2 border-blue-200 focus:border-blue-500 rounded font-semibold text-sm"
                    />
                  </div>
                )}
              </div>

            </div>

            {/* Balance Cash Flow Radios */}
            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-12 md:col-span-6">
                <label className="text-blue-900 font-semibold block mb-1 text-sm">Balance cash Flow</label>
                <div className="flex items-center gap-6 rounded border-2 border-blue-200 px-4 py-2 w-fit bg-white shadow-sm">
                  <label className="flex items-center gap-2 text-blue-900 font-medium select-none cursor-pointer">
                    <input
                      type="radio"
                      name="bal_flow"
                      checked={balanceCashFlow === "In"}
                      onChange={() => setBalanceCashFlow("In")}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span>In</span>
                  </label>
                  <label className="flex items-center gap-2 text-blue-900 font-medium select-none cursor-pointer">
                    <input
                      type="radio"
                      name="bal_flow"
                      checked={balanceCashFlow === "Out"}
                      onChange={() => setBalanceCashFlow("Out")}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Out</span>
                  </label>
                </div>
              </div>

              {/* WhatsApp Option banner */}
              <div className="col-span-12 md:col-span-6 flex justify-end">
                <div className="flex items-center gap-3 bg-white p-3 rounded-lg border-2 border-green-400 shadow-sm text-green-600 font-semibold text-sm">
                  <input
                    type="checkbox"
                    checked={sendWhatsApp}
                    onChange={(e) => setSendWhatsApp(e.target.checked)}
                    className="h-5 w-5 rounded border-green-400 text-green-600 focus:ring-green-500"
                  />
                  <span className="flex items-center gap-1.5">
                    <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.73-1.45L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.42 9.864-9.864.002-2.637-1.03-5.118-2.905-6.993-1.876-1.875-4.359-2.907-6.999-2.907-5.438 0-9.863 4.421-9.867 9.865-.001 1.734.487 3.426 1.419 4.903l-.936 3.425 3.507-.922zm9.952-6.84c-.267-.134-1.58-.779-1.824-.868-.244-.089-.422-.134-.599.134-.177.268-.687.868-.843 1.046-.156.177-.311.2-.578.066-.267-.134-1.127-.415-2.147-1.326-.793-.707-1.329-1.58-1.485-1.847-.156-.268-.017-.412.117-.545.121-.12.267-.312.4-.469.134-.156.177-.267.267-.446.089-.178.044-.334-.022-.469-.066-.134-.599-1.443-.822-1.979-.217-.523-.456-.453-.624-.461-.161-.008-.346-.01-.53-.01s-.485.069-.739.346c-.253.277-.968.946-.968 2.3 0 1.355.986 2.661 1.123 2.846.137.185 1.942 2.966 4.706 4.159.658.284 1.171.453 1.571.58.66.21 1.261.18 1.737.109.53-.08 1.58-.646 1.802-1.27.222-.624.222-1.157.156-1.268-.067-.111-.244-.177-.511-.311z"/>
                    </svg>
                    <span>Send via WhatsApp</span>
                  </span>
                </div>
              </div>
            </div>

            {/* DYNAMIC OUT SECTION (Renders when balanceCashFlow is Out) */}
            {balanceCashFlow === "Out" && (
              <div className="grid grid-cols-12 gap-4 border-2 border-blue-200 p-4 rounded-lg bg-blue-100/30">
                
                {/* 1. Cash Out Handed Over */}
                <div className="col-span-12 border-2 border-blue-200 p-4 rounded-lg bg-white relative">
                  <div className="absolute top-3 right-3">
                    <Button
                      type="button"
                      size="icon"
                      onClick={addCashOutRow}
                      className="bg-blue-600 hover:bg-blue-700 text-white h-8 w-8 rounded shadow-sm"
                      title="Add Cash Out Row"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-3 mt-2">
                    {cashOutTransactions.map((item) => (
                      <div key={item.id} className="flex flex-wrap md:flex-nowrap items-end gap-3">
                        <div className="flex-1 min-w-[150px]">
                          <label className="text-blue-900 font-semibold text-xs block mb-1">Choose Type</label>
                          <select
                            value={item.type}
                            onChange={(e) => updateCashOutRow(item.id, "type", e.target.value)}
                            className="w-full bg-white text-gray-900 h-10 px-3 rounded text-sm font-semibold border-2 border-blue-200 focus:border-blue-500"
                          >
                            <option value="">Choose Type</option>
                            <option value="Bank Deposit">Bank Deposit</option>
                            <option value="Handed to Owner">Handed to Owner</option>
                            <option value="Handed to Office">Handed to Office</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>

                        <div className="flex-1 min-w-[150px]">
                          <label className="text-blue-900 font-semibold text-xs block mb-1">Handed to</label>
                          <Input
                            type="text"
                            placeholder="HandedTo"
                            value={item.handedTo}
                            onChange={(e) => updateCashOutRow(item.id, "handedTo", e.target.value)}
                            className="bg-white text-gray-900 h-10 border-2 border-blue-200 focus:border-blue-500 rounded text-sm font-semibold"
                          />
                        </div>

                        <div className="w-32">
                          <label className="text-blue-900 font-semibold text-xs block mb-1">Amount</label>
                          <Input
                            type="number"
                            placeholder="Amount"
                            value={item.amount || ""}
                            onChange={(e) => updateCashOutRow(item.id, "amount", Number(e.target.value) || 0)}
                            className="bg-white text-gray-900 h-10 border-2 border-blue-200 focus:border-blue-500 rounded text-sm font-semibold"
                          />
                        </div>

                        <div className="flex-1 min-w-[200px]">
                          <label className="text-blue-900 font-semibold text-xs block mb-1">Note</label>
                          <Input
                            type="text"
                            placeholder="Note"
                            value={item.note}
                            onChange={(e) => updateCashOutRow(item.id, "note", e.target.value)}
                            className="bg-white text-gray-900 h-10 border-2 border-blue-200 focus:border-blue-500 rounded text-sm font-semibold"
                          />
                        </div>

                        <Button
                          type="button"
                          size="icon"
                          variant="destructive"
                          onClick={() => removeCashOutRow(item.id)}
                          className="h-10 w-10 shrink-0 mb-[1px]"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 2. Vendor Payments */}
                <div className="col-span-12 border-2 border-blue-200 p-4 rounded-lg bg-white">
                  <div className="space-y-3">
                    {vendorPayments.map((item, index) => (
                      <div key={item.id} className="flex flex-wrap md:flex-nowrap items-end gap-3">
                        <div className="flex-1 min-w-[200px]">
                          <label className="text-blue-900 font-semibold text-xs block mb-1">Vendor</label>
                          <select
                            value={item.vendorId}
                            onChange={(e) => updateVendorPaymentRow(item.id, "vendorId", e.target.value)}
                            className="w-full bg-white text-gray-900 h-10 px-3 rounded text-sm font-semibold border-2 border-blue-200 focus:border-blue-500"
                          >
                            <option value="">Choose Vendor</option>
                            {(vendorsList || []).map((v: any) => (
                              <option key={v.id || v._id} value={v.id || v._id}>
                                {v.vendorName || v.vendor_name || "Unnamed Vendor"}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="w-44">
                          <label className="text-blue-900 font-semibold text-xs block mb-1">Paid Amount</label>
                          <Input
                            type="number"
                            placeholder="Paid Amount"
                            value={item.paidAmount || ""}
                            onChange={(e) => updateVendorPaymentRow(item.id, "paidAmount", Number(e.target.value) || 0)}
                            className="bg-white text-gray-900 h-10 border-2 border-blue-200 focus:border-blue-500 rounded text-sm font-semibold"
                          />
                        </div>

                        <div className="flex-1 min-w-[250px]">
                          <label className="text-blue-900 font-semibold text-xs block mb-1">Note</label>
                          <Input
                            type="text"
                            placeholder="Note"
                            value={item.note}
                            onChange={(e) => updateVendorPaymentRow(item.id, "note", e.target.value)}
                            className="bg-white text-gray-900 h-10 border-2 border-blue-200 focus:border-blue-500 rounded text-sm font-semibold"
                          />
                        </div>

                        {index === 0 ? (
                          <Button
                            type="button"
                            size="icon"
                            onClick={addVendorPaymentRow}
                            className="bg-blue-600 hover:bg-blue-700 text-white h-10 w-10 shrink-0 mb-[1px]"
                            title="Add Vendor Row"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            size="icon"
                            variant="destructive"
                            onClick={() => removeVendorPaymentRow(item.id)}
                            className="h-10 w-10 shrink-0 mb-[1px]"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            )}

            <div className="grid grid-cols-12 gap-4">
              
              {/* Day Closing Balance with Denomination button */}
              <div className="col-span-12 md:col-span-6 flex flex-col gap-2">
                <label className="text-blue-900 font-semibold block mb-1 text-sm">Day Closing Bal. Cash(Inhand)</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Day Closing Bal. Cash(Inhand)"
                    className="bg-white text-gray-900 h-10 border-2 border-blue-200 focus:border-blue-500 rounded flex-1 font-semibold text-sm"
                    {...closingBalanceField}
                    onFocus={(e) => { if (e.target.value === "NaN" || e.target.value === "0") { setValue("closing_balance", "" as any); } }}
                  />
                  <Button
                    type="button"
                    className="bg-[#e67e22] hover:bg-[#d35400] text-white font-bold h-10 rounded text-sm px-4 shrink-0"
                    onClick={() => alert("Denominations window coming soon")}
                  >
                    DENOMINATIONS
                  </Button>
                </div>
                {employeeCashSummary?.hasRecords && (
                  <p className="text-xs font-semibold text-emerald-700">
                    Employee cash collected: ₹{formatInr(employeeCashSummary.collectedCash)}
                    {employeeCashSummary.recordedShortage > 0 && (
                      <span className="ml-2 text-red-600">(Employee shortage: ₹{formatInr(employeeCashSummary.recordedShortage)})</span>
                    )}
                  </p>
                )}
                <p className={`text-xs font-semibold ${hasClosingBalance && difference > 0 ? "text-red-600" : hasClosingBalance && difference < 0 ? "text-green-600" : "text-blue-600"}`}>
                  Expected Cash: ₹{formatInr(expectedCash)}
                  {hasClosingBalance && (
                    <span className="ml-2 font-bold">
                      ({difference >= 0 ? "Shortage" : "Excess"} vs expected cash: ₹{formatInr(Math.abs(difference))})
                    </span>
                  )}
                </p>
                {errors.closing_balance && (<p className="text-xs text-red-600">{errors.closing_balance.message}</p>)}
              </div>

              {/* Note */}
              <div className="col-span-12 md:col-span-6">
                <label className="text-blue-900 font-semibold block mb-1 text-sm">Note</label>
                <Textarea
                  rows={2}
                  placeholder="Note"
                  className="bg-white text-gray-900 border-2 border-blue-200 focus:border-blue-500 rounded text-sm font-semibold"
                  {...register("notes")}
                />
              </div>

            </div>

            {/* Upload Row */}
            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-12 md:col-span-6">
                <label className="text-blue-900 font-semibold block mb-1 text-sm">
                  Upload Slips <span className="text-xs text-blue-600 font-normal">(Each file size should be below 5MB)</span>
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    readOnly
                    placeholder="No file selected"
                    className="bg-white text-gray-900 h-10 border-2 border-blue-200 focus:border-blue-500 rounded flex-1 text-sm font-semibold"
                  />
                  <Button
                    type="button"
                    className="bg-[#2ecc71] hover:bg-[#27ae60] text-white font-bold h-10 rounded text-sm px-4 shrink-0"
                  >
                    Browse
                  </Button>
                </div>
              </div>
            </div>

            {/* Confirm Submit */}
            <div className="flex justify-center pt-2">
              <Button
                type="submit"
                className="bg-[#84cc16] hover:bg-[#65a30d] text-white font-bold h-11 px-12 rounded-full shadow-md text-sm tracking-wider"
                disabled={saveSettlement.isPending}
              >
                {saveSettlement.isPending ? "Processing..." : (editingId ? "UPDATE" : "CONFIRM")}
              </Button>
            </div>

          </form>
        </CardContent>
      </Card>

      {/* Recent settlements table */}
      <div className="rounded-xl overflow-hidden border border-blue-200 shadow-lg">
        {/* Card Header */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-600 px-5 py-4 flex items-center justify-between">
          <h2 className="text-white font-bold text-base tracking-wide flex items-center gap-2">
            <span className="inline-block w-2 h-5 rounded-sm bg-white/60 mr-1" />
            Recent Settlements
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint} className="px-3 py-1.5 text-xs font-medium rounded-md bg-white/15 hover:bg-white/25 text-white border border-white/30 transition-colors">Direct Print</button>
            <button onClick={() => {}} className="px-3 py-1.5 text-xs font-medium rounded-md bg-white/15 hover:bg-white/25 text-white border border-white/30 transition-colors">Copy</button>
            <button onClick={handleCSV} className="px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-500/80 hover:bg-emerald-500 text-white border border-emerald-400/40 transition-colors">CSV</button>
            <button onClick={() => {}} className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-500/80 hover:bg-red-500 text-white border border-red-400/40 transition-colors">PDF</button>
            <button onClick={handlePrint} className="px-3 py-1.5 text-xs font-medium rounded-md bg-white/15 hover:bg-white/25 text-white border border-white/30 transition-colors">Print</button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white">
          <RecentSettlements
            data={recentSettlements || []}
            isLoading={settlementsLoading}
            onEdit={(row: any) => {
              setEditingId(String(row.id));
              setSelectedDate(String(row.settlement_date).slice(0, 10));
              
              // Extract extra fields from notes if saved in notes format
              let savedPetty = 0;
              let savedInflow = 0;
              let savedFlow: "In" | "Out" = "In";
              let savedCashOut: any[] = [];
              let savedVendors: any[] = [];
              let savedManualBalance: number | null = null;

              if (row.notes) {
                const detailsMatch = row.notes.match(/\[DaySettlementDetails:(\{[\s\S]*\})\]/);
                if (detailsMatch) {
                  try {
                    const parsed = JSON.parse(detailsMatch[1]);
                    savedPetty = parsed.pettyCash || 0;
                    savedInflow = parsed.cashInflowAmount || 0;
                    savedFlow = parsed.balanceCashFlow || "In";
                    savedCashOut = parsed.cashOutTransactions || [];
                    savedVendors = parsed.vendorPayments || [];
                    savedManualBalance = parsed.manualTransactionBalance !== undefined ? parsed.manualTransactionBalance : null;
                  } catch (e) {
                    console.error("Failed to parse settlement details JSON", e);
                  }
                } else {
                  // Fallback to old format
                  const pettyMatch = row.notes.match(/\[Petty Cash:\s*([\d.]+)\]/);
                  const inflowMatch = row.notes.match(/\[Cash Inflow:\s*([\d.]+)\]/);
                  const flowMatch = row.notes.match(/\[Flow:\s*(In|Out)\]/);
                  if (pettyMatch) savedPetty = Number(pettyMatch[1]) || 0;
                  if (inflowMatch) savedInflow = Number(inflowMatch[1]) || 0;
                  if (flowMatch) savedFlow = flowMatch[1] as "In" | "Out";
                }
              }

              setPettyCash(savedPetty);
              setCashInflowAmount(savedInflow);
              setAnyCashInflow(savedInflow > 0);
              setBalanceCashFlow(savedFlow);
              setCashOutTransactions(savedCashOut.length > 0 ? savedCashOut : [{ id: "1", type: "", handedTo: "", amount: 0, note: "" }]);
              setVendorPayments(savedVendors.length > 0 ? savedVendors : [{ id: "1", vendorId: "", paidAmount: 0, note: "" }]);
              setManualTransactionBalance(savedManualBalance);

              reset({
                opening_balance: Number(row.opening_balance || 0),
                meter_sale: Number(row.meter_sale || 0),
                lubricant_sale: Number(row.lubricant_sale || 0),
                credit_amount: Number(row.credit_amount || 0),
                total_sale: Number(row.total_sale || 0),
                expenses: Number(row.expenses || 0),
                cash_to_bank: Number(row.cash_to_bank || 0),
                closing_balance: Number(row.closing_balance || 0),
                shortage: Number(row.shortage || 0),
                notes: row.notes ? row.notes.replace(/\[DaySettlementDetails:\{[\s\S]*?\}\]|\[Petty Cash:.*?\]|\[Cash Inflow:.*?\]|\[Flow:.*?\]/g, "").trim() : "",
              });
            }}
            onDeleted={() => queryClient.invalidateQueries({ queryKey: ["/api/day-settlements"] })}
          />
        </div>
      </div>

      {/* Summary Panel Confirmation Modal */}
      {isSummaryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl overflow-hidden border border-gray-200 animate-in fade-in zoom-in-95 duration-150">
            
            {/* Header */}
            <div className="bg-blue-600 text-white px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold tracking-wide">Summary Panel</h2>
              <button 
                type="button" 
                onClick={handleModalClose} 
                className="text-white/80 hover:text-white text-2xl font-bold transition-colors leading-none"
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 grid grid-cols-12 gap-6 bg-gray-50/50">
              
              {/* Left Column */}
              <div className="col-span-12 md:col-span-7 space-y-6">
                
                {/* 1. Product Table */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="bg-[#ff7979] text-white text-xs font-bold uppercase">
                        <th className="py-2.5 px-4">Product</th>
                        <th className="py-2.5 px-4 text-right">Sale Qty (ltr.)</th>
                        <th className="py-2.5 px-4 text-right">Sale Value (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(() => {
                        const productsList = ["Motor Spirit", "High Speed Desi", "Extra Premium"];
                        const prodSales = productsList.map(name => {
                          const grouped = Object.entries(productGroup).find(([k]) => mapProductName(k) === name);
                          return {
                            name,
                            qty: grouped ? grouped[1].qty : 0,
                            value: grouped ? grouped[1].value : 0
                          };
                        });

                        const totalMeterQty = prodSales.reduce((sum, p) => sum + p.qty, 0);
                        const totalMeterVal = prodSales.reduce((sum, p) => sum + p.value, 0);
                        const lubSaleValue = salesData?.lubricantSales || 0;
                        const totalSaleValue = totalMeterVal + lubSaleValue;

                        return (
                          <>
                            {prodSales.map(p => (
                              <tr key={p.name} className="hover:bg-gray-50/50 transition-colors">
                                <td className="py-2 px-4 font-semibold text-gray-700">{p.name}</td>
                                <td className="py-2 px-4 text-right text-gray-900 font-medium">{formatInr(p.qty)}</td>
                                <td className="py-2 px-4 text-right text-gray-900 font-medium">{formatInr(p.value)}</td>
                              </tr>
                            ))}
                            <tr className="bg-gray-100/50 font-bold border-t border-gray-200">
                              <td className="py-2.5 px-4 text-gray-800 text-xs uppercase">METER SALE</td>
                              <td className="py-2.5 px-4"></td>
                              <td className="py-2.5 px-4 text-right text-gray-900">{formatInr(totalMeterVal)}</td>
                            </tr>
                            <tr className="bg-gray-100/50 font-bold border-t border-gray-200">
                              <td className="py-2.5 px-4 text-gray-800 text-xs uppercase">LUBRICANT SALE</td>
                              <td className="py-2.5 px-4"></td>
                              <td className="py-2.5 px-4 text-right text-gray-900">{formatInr(lubSaleValue)}</td>
                            </tr>
                            <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                              <td className="py-2.5 px-4 text-gray-900 text-sm uppercase">TOTAL SALE VALUE(₹)</td>
                              <td className="py-2.5 px-4"></td>
                              <td className="py-2.5 px-4 text-right text-gray-955 text-sm">{formatInr(totalSaleValue)}</td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* 2. Swipe Table */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="bg-[#58b97d] text-white text-xs font-bold uppercase">
                        <th className="py-2.5 px-4">Swipe</th>
                        <th className="py-2.5 px-4 text-right">Total (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(() => {
                        const modesList = ["phone pe", "CCMS", "ICICI CARD", "ICICI SCAN", "account pay"];
                        const swipeGroup: Record<string, number> = {};
                        
                        if (salesData && salesData._rawSwipeSales) {
                          for (const s of salesData._rawSwipeSales) {
                            const mName = mapSwipeMode(s.swipe_mode || s.swipeMode || "");
                            const val = Number(s.amount || 0);
                            swipeGroup[mName] = (swipeGroup[mName] || 0) + val;
                          }
                        }

                        const rowsList = modesList.map(name => ({
                          name,
                          value: swipeGroup[name] || 0
                        }));

                        Object.entries(swipeGroup).forEach(([k, v]) => {
                          if (!modesList.includes(k)) {
                            rowsList.push({ name: k, value: v });
                          }
                        });

                        return (
                          <>
                            {rowsList.map(r => (
                              <tr key={r.name} className="hover:bg-gray-50/50 transition-colors">
                                <td className="py-2 px-4 font-semibold text-gray-700">{r.name}</td>
                                <td className="py-2 px-4 text-right text-gray-900 font-medium">{formatInr(r.value)}</td>
                              </tr>
                            ))}
                            <tr className="bg-gray-100 font-bold border-t border-gray-200">
                              <td className="py-2.5 px-4 text-gray-900 uppercase">Total</td>
                              <td className="py-2.5 px-4 text-right text-gray-950">{formatInr(salesData?.swipeSales || 0)}</td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* 3. Recovery Table */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="bg-[#8d7060] text-white text-xs font-bold uppercase">
                        <th className="py-2.5 px-4">Recovery Mode</th>
                        <th className="py-2.5 px-4 text-right">Amount (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(() => {
                        const modesList = ["CASH", "BANK", "SWIPE", "IOCL"];
                        const recoveryBreakdown = recoveriesData || { CASH: 0, BANK: 0, SWIPE: 0, IOCL: 0, TOTAL: 0 };
                        
                        return (
                          <>
                            {modesList.map(name => {
                              const val = recoveryBreakdown[name] || 0;
                              return (
                                <tr key={name} className="hover:bg-gray-50/50 transition-colors">
                                  <td className="py-2 px-4 font-semibold text-gray-700">{name}</td>
                                  <td className="py-2 px-4 text-right text-gray-900 font-medium">{formatInr(val)}</td>
                                </tr>
                              );
                            })}
                            <tr className="bg-gray-100 font-bold border-t border-gray-200">
                              <td className="py-2.5 px-4 text-gray-900 uppercase">TOTAL RECOVERY (₹)</td>
                              <td className="py-2.5 px-4 text-right text-gray-955">{formatInr(recoveryBreakdown.TOTAL || 0)}</td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>

              </div>

              {/* Right Column */}
              <div className="col-span-12 md:col-span-5">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden h-fit">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="bg-[#26ae90] text-white text-xs font-bold uppercase">
                        <th colSpan={2} className="py-2.5 px-4 text-center">Day Final Settlement (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(() => {
                        const saleAmt = (salesData?.totalSales || 0);
                        const cashRec = recoveriesData?.CASH || 0;
                        const cashInf = cashInflowAmount;
                        const sumInflow = saleAmt + cashRec + cashInf;

                        const swipes = (salesData?.swipeSales || 0);
                        const credits = (salesData?.creditSales || 0);
                        const expenses = (expensesData || 0);
                        const cashInHandVal = pendingData ? Number(pendingData.closing_balance) || 0 : 0;
                        const shortage = expectedCash - cashInHandVal;

                        return (
                          <>
                            <tr className="hover:bg-gray-50/50 transition-colors">
                              <td className="py-3 px-4 font-semibold text-gray-600">SALE AMOUNT</td>
                              <td className="py-3 px-4 text-right text-gray-900 font-medium">{formatInr(saleAmt)}</td>
                            </tr>
                            <tr className="hover:bg-gray-50/50 transition-colors">
                              <td className="py-3 px-4 font-semibold text-gray-600">CASH RECOVERY</td>
                              <td className="py-3 px-4 text-right text-gray-900 font-medium">{formatInr(cashRec)}</td>
                            </tr>
                            <tr className="hover:bg-gray-50/50 transition-colors">
                              <td className="py-3 px-4 font-semibold text-gray-600">CASH INFLOW</td>
                              <td className="py-3 px-4 text-right text-gray-900 font-medium">{formatInr(cashInf)}</td>
                            </tr>
                            <tr className="bg-gray-100/50">
                              <td colSpan={2} className="py-1 px-4 text-center text-gray-400 font-medium">----------------------------------------</td>
                            </tr>
                            <tr className="bg-gray-100 font-bold">
                              <td className="py-3 px-4 text-gray-900">Total</td>
                              <td className="py-3 px-4 text-right text-gray-955">{formatInr(sumInflow)}</td>
                            </tr>
                            <tr className="bg-gray-100/50">
                              <td colSpan={2} className="py-1 px-4 text-center text-gray-400 font-medium">----------------------------------------</td>
                            </tr>
                            <tr className="hover:bg-gray-50/50 transition-colors">
                              <td className="py-3 px-4 font-semibold text-gray-600">SWIPES</td>
                              <td className="py-3 px-4 text-right text-gray-900 font-medium">{formatInr(swipes)}</td>
                            </tr>
                            <tr className="hover:bg-gray-50/50 transition-colors">
                              <td className="py-3 px-4 font-semibold text-gray-600">CREDITS</td>
                              <td className="py-3 px-4 text-right text-gray-900 font-medium">{formatInr(credits)}</td>
                            </tr>
                            <tr className="hover:bg-gray-50/50 transition-colors">
                              <td className="py-3 px-4 font-semibold text-gray-600">EXPENSES</td>
                              <td className="py-3 px-4 text-right text-gray-900 font-medium">{formatInr(expenses)}</td>
                            </tr>
                            {totalOutSectionAmount > 0 && (
                              <tr className="hover:bg-gray-50/50 transition-colors">
                                <td className="py-3 px-4 font-semibold text-gray-600">CASH OUTFLOWS (SECTION)</td>
                                <td className="py-3 px-4 text-right text-gray-900 font-medium">{formatInr(totalOutSectionAmount)}</td>
                              </tr>
                            )}
                            {employeeCashSummary?.hasRecords && (
                              <tr className="hover:bg-gray-50/50 transition-colors">
                                <td className="py-3 px-4 font-semibold text-gray-600">EMPLOYEE CASH COLLECTED</td>
                                <td className="py-3 px-4 text-right text-gray-900 font-medium">{formatInr(employeeCashSummary.collectedCash)}</td>
                              </tr>
                            )}
                            <tr className="hover:bg-gray-50/50 transition-colors">
                              <td className="py-3 px-4 font-semibold text-gray-600">SHORTAGE</td>
                              <td className={`py-3 px-4 text-right font-semibold ${shortage > 0 ? 'text-red-500' : shortage < 0 ? 'text-green-600' : 'text-gray-900'}`}>{formatInr(Math.abs(shortage))}</td>
                            </tr>
                            <tr className="bg-[#2ecc71]/15">
                              <td className="py-3.5 px-4 font-bold text-[#27ae60] text-sm">CASH IN HAND (₹)</td>
                              <td className="py-3.5 px-4 text-right font-bold text-white bg-[#2ecc71] text-base rounded-r">{formatInr(cashInHandVal)}</td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>

            {/* Footer Buttons */}
            <div className="bg-white border-t border-gray-150 px-6 py-4 flex items-center justify-center gap-4">
              <Button
                type="button"
                onClick={handleModalConfirm}
                className="bg-[#8cd35c] hover:bg-[#7bc24c] text-white font-bold h-11 px-8 rounded flex items-center gap-2 shadow"
              >
                <CheckCircle className="h-5 w-5" />
                <span>OK</span>
              </Button>
              <Button
                type="button"
                onClick={handleModalClose}
                className="bg-[#f35454] hover:bg-[#e04343] text-white font-bold h-11 px-8 rounded flex items-center gap-2 shadow"
              >
                <span>&times;</span>
                <span>Close</span>
              </Button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

function RecentSettlements({ data, isLoading, onEdit, onDeleted }: { data: any[]; isLoading: boolean; onEdit: (row: any) => void; onDeleted: () => void; }) {
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) return <div className="px-5 py-8 text-sm text-blue-400">Loading...</div>;
  if (!data || data.length === 0) return <div className="px-5 py-8 text-sm text-gray-400">No settlements recorded yet.</div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 text-xs font-semibold uppercase tracking-wide">
            <th className="py-3 px-4 text-left">S.No</th>
            <th className="py-3 px-4 text-left">Date</th>
            <th className="py-3 px-4 text-left">DayBusi Cash In Hand</th>
            <th className="py-3 px-4 text-left">Open Balance</th>
            <th className="py-3 px-4 text-left">Close Balance</th>
            <th className="py-3 px-4 text-left">Note</th>
            <th className="py-3 px-4 text-left">Action</th>
            <th className="py-3 px-4 text-left">Settlement</th>
            <th className="py-3 px-4 text-left">Pictures</th>
            <th className="py-3 px-4 text-left">User</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row: any, index: number) => (
            <React.Fragment key={row.id || `${row.settlement_date}-${index}`}>
              <tr className="border-b border-blue-50 hover:bg-blue-50/60 transition-colors group">
                <td className="py-3 px-4 text-gray-500 text-xs">{index + 1}</td>
                <td className="py-3 px-4 font-semibold text-blue-700">{formatDate(row.settlement_date)}</td>
                <td className="py-3 px-4">
                  <span className="font-bold text-emerald-600 text-base">
                    ₹{(() => {
                      const transactionBalance = row.manual_transaction_balance !== null && row.manual_transaction_balance !== undefined
                        ? Number(row.manual_transaction_balance)
                        : (Number(row.meter_sale || 0) + Number(row.lubricant_sale || 0)
                           - Number(row.credit_amount || 0) - Number(row.swipe_amount || 0)
                           - Number(row.expenses || 0) - Number(row.cash_to_bank || 0)
                           + Number(row.recovery_amount || 0));

                      const cashInHand = Number(row.opening_balance || 0)
                        + Number(row.petty_cash || 0) + Number(row.cash_inflow_amount || 0)
                        + transactionBalance
                        - Number(row.total_out_section_amount || 0);
                      return cashInHand.toLocaleString('en-IN', { minimumFractionDigits: 2 });
                    })()}
                  </span>
                </td>
                <td className="py-3 px-4 text-gray-600">₹{Number(row.opening_balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                <td className="py-3 px-4 text-gray-600">₹{Number(row.closing_balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                <td className="py-3 px-4 text-gray-500 text-xs">{cleanNotes(row.notes) || <span className="text-gray-300">—</span>}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onEdit(row)}
                      className="px-3 py-1 text-xs font-semibold rounded-md border border-blue-500 text-blue-600 hover:bg-blue-600 hover:text-white transition-colors"
                    >Edit</button>
                    <button
                      disabled={deletingId === String(row.id)}
                      onClick={async () => {
                        if (!row.id) return;
                        if (!confirm('Delete this settlement?')) return;
                        try {
                          setDeletingId(String(row.id));
                          const r = await fetchJSON(`/api/day-settlements/${row.id}`, { method: 'DELETE' });
                          const txt = await r.text();
                          let j: any = {}; try { j = JSON.parse(txt || '{}'); } catch { }
                          if (!r.ok || !j?.ok) throw new Error(j?.error || `Server error (${r.status})`);
                          queryClient.setQueryData(["/api/day-settlements"], (old: any) => {
                            const arr = Array.isArray(old) ? old : [];
                            return arr.filter((it: any) => String(it.id) !== String(row.id));
                          });
                          onDeleted();
                        } catch (e: any) {
                          alert(e?.message || 'Delete failed');
                        } finally { setDeletingId(null); }
                      }}
                      className="px-3 py-1 text-xs font-semibold rounded-md border border-red-500 text-red-600 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
                    >{deletingId === String(row.id) ? '...' : 'Delete'}</button>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <button
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-blue-100 hover:bg-blue-600 hover:text-white text-blue-600 transition-colors text-xs font-bold"
                    onClick={() => setExpandedId(expandedId === String(row.id) ? null : String(row.id))}
                    title="View settlement details"
                  >{expandedId === String(row.id) ? '▼' : '▶'}</button>
                </td>
                <td className="py-3 px-4 text-gray-400 text-xs">—</td>
                <td className="py-3 px-4 text-gray-500 text-xs">{(() => {
                  const author = row.created_by || 'Super Admin';
                  const d = row.created_at ? new Date(row.created_at) : null;
                  const raw = d ? d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }) : '';
                  const fmt = raw ? raw.split('/').join('-') : '';
                  return `Created: ${author}${fmt ? ' ' + fmt : ''}`;
                })()}</td>
              </tr>
              {expandedId === String(row.id) && (
                <tr>
                  <td colSpan={10} className="px-0 py-0">
                    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white px-6 py-4">
                      {/* Header */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-1 h-6 rounded-full bg-blue-300" />
                        <span className="font-bold text-base text-white tracking-wide">Settlement {formatDate(row.settlement_date)}</span>
                      </div>
                      {/* Main grid */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {/* Opening */}
                        <div className="bg-white/10 rounded-lg px-4 py-2">
                          <div className="text-blue-200 text-xs mb-0.5">Opening Handcash</div>
                          <div className="font-semibold text-white">₹{Number(row.opening_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                        </div>
                        {/* Petty Cash */}
                        {Number(row.petty_cash || 0) > 0 && (
                          <div className="bg-white/10 rounded-lg px-4 py-2">
                            <div className="text-blue-200 text-xs mb-0.5">Petty Cash</div>
                            <div className="font-semibold text-white">₹{Number(row.petty_cash || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                          </div>
                        )}
                        {/* Manual Override Warning Card */}
                        {row.manual_transaction_balance !== null && row.manual_transaction_balance !== undefined && (
                          <div className="bg-amber-500/20 border border-amber-400/30 rounded-lg px-4 py-2 col-span-2 md:col-span-3">
                            <div className="text-amber-200 text-xs mb-0.5 font-bold uppercase tracking-wider">⚠️ Manual Override Active</div>
                            <div className="text-xs text-blue-100 mb-0.5">Business transaction cash balance was entered manually:</div>
                            <div className="font-semibold text-amber-300">₹{Number(row.manual_transaction_balance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                          </div>
                        )}
                        {/* Meter Sale */}
                        <div className="bg-white/10 rounded-lg px-4 py-2">
                          <div className="text-blue-200 text-xs mb-0.5">Meter Sale</div>
                          <div className="font-semibold text-emerald-300">₹{Number(row.meter_sale || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                        </div>
                        {/* Lubricant */}
                        <div className="bg-white/10 rounded-lg px-4 py-2">
                          <div className="text-blue-200 text-xs mb-0.5">Lubricant Sale</div>
                          <div className="font-semibold text-emerald-300">₹{Number(row.lubricant_sale || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                        </div>
                        {/* Credit */}
                        <div className="bg-orange-500/20 border border-orange-400/30 rounded-lg px-4 py-2">
                          <div className="text-orange-200 text-xs mb-0.5">Credit (deducted)</div>
                          <div className="font-semibold text-orange-300">− ₹{Number(row.credit_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                        </div>
                        {/* Swipe */}
                        <div className="bg-sky-500/20 border border-sky-400/30 rounded-lg px-4 py-2">
                          <div className="text-sky-200 text-xs mb-0.5">💳 Swipe / non-cash (deducted)</div>
                          <div className="font-semibold text-sky-300">− ₹{Number(row.swipe_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                        </div>
                        {/* Total Sale */}
                        <div className="bg-white/10 rounded-lg px-4 py-2">
                          <div className="text-blue-200 text-xs mb-0.5">Total Sale (Meter + Lub)</div>
                          <div className="font-semibold text-white">₹{(Number(row.meter_sale || 0) + Number(row.lubricant_sale || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                        </div>
                         {/* Expenses */}
                         <div className="bg-white/10 rounded-lg px-4 py-2">
                           <div className="text-blue-200 text-xs mb-0.5">Expenses</div>
                           <div className="font-semibold text-red-300">₹{Number(row.expenses || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                         </div>
                         {/* Credit Recovery */}
                         {Number(row.recovery_amount || 0) > 0 && (
                           <div className="bg-emerald-500/20 border border-emerald-400/30 rounded-lg px-4 py-2">
                             <div className="text-emerald-200 text-xs mb-0.5">Credit Recovery (added)</div>
                             <div className="font-semibold text-emerald-300">+ ₹{Number(row.recovery_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                           </div>
                         )}
                         {/* Cash Inflow */}
                         {Number(row.cash_inflow_amount || 0) > 0 && (
                           <div className="bg-emerald-500/20 border border-emerald-400/30 rounded-lg px-4 py-2">
                             <div className="text-emerald-200 text-xs mb-0.5">Cash Inflow (added)</div>
                             <div className="font-semibold text-emerald-300">+ ₹{Number(row.cash_inflow_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                           </div>
                         )}
                         {/* Cash to Bank */}
                         {Number(row.cash_to_bank || 0) > 0 && (
                           <div className="bg-white/10 rounded-lg px-4 py-2">
                             <div className="text-blue-200 text-xs mb-0.5">🏦 Cash to Bank</div>
                             <div className="font-semibold text-emerald-300">₹{Number(row.cash_to_bank || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                           </div>
                         )}
                         {/* Outflows Section */}
                         {Number(row.total_out_section_amount || 0) > 0 && (
                           <div className="bg-orange-500/20 border border-orange-400/30 rounded-lg px-4 py-2">
                             <div className="text-orange-200 text-xs mb-0.5">Outflows (deducted)</div>
                             <div className="font-semibold text-orange-300">− ₹{Number(row.total_out_section_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                           </div>
                         )}
                         {/* Shortage */}
                         <div className="bg-white/10 rounded-lg px-4 py-2">
                           <div className="text-blue-200 text-xs mb-0.5">Shortage / Excess</div>
                           <div className={`font-semibold ${Number(row.shortage || 0) < 0 ? 'text-red-400' : 'text-emerald-300'}`}>₹{Number(row.shortage || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                         </div>
                         {/* Closing */}
                         <div className="bg-white/10 rounded-lg px-4 py-2">
                           <div className="text-blue-200 text-xs mb-0.5">Day Closing Bal. cash</div>
                           <div className="font-semibold text-white">₹{Number(row.closing_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                         </div>
                       </div>
                       
                       {/* Detailed Lists of Outflows */}
                       {((row.cash_out_transactions && row.cash_out_transactions.length > 0 && row.cash_out_transactions.some((item: any) => item.amount > 0)) || 
                         (row.vendor_payments && row.vendor_payments.length > 0 && row.vendor_payments.some((item: any) => item.paidAmount > 0))) && (
                         <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white/5 border border-white/10 rounded-xl px-5 py-3">
                           {row.cash_out_transactions && row.cash_out_transactions.length > 0 && row.cash_out_transactions.some((item: any) => item.amount > 0) && (
                             <div>
                               <div className="text-blue-200 text-xs mb-1.5 font-bold uppercase tracking-wider">💳 Cash Handed Over Details:</div>
                               <div className="space-y-1 text-xs">
                                 {row.cash_out_transactions.map((item: any, idx: number) => item.amount > 0 && (
                                   <div key={idx} className="flex justify-between border-b border-white/5 pb-0.5 pr-2">
                                     <span>{item.type || 'Other'} ({item.handedTo || 'N/A'}){item.note ? ` [${item.note}]` : ''}:</span>
                                     <span className="font-semibold text-emerald-300">₹{Number(item.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                   </div>
                                 ))}
                               </div>
                             </div>
                           )}
                           {row.vendor_payments && row.vendor_payments.length > 0 && row.vendor_payments.some((item: any) => item.paidAmount > 0) && (
                             <div>
                               <div className="text-blue-200 text-xs mb-1.5 font-bold uppercase tracking-wider">🏢 Vendor Payments Details:</div>
                               <div className="space-y-1 text-xs">
                                 {row.vendor_payments.map((item: any, idx: number) => item.paidAmount > 0 && (
                                   <div key={idx} className="flex justify-between border-b border-white/5 pb-0.5 pr-2">
                                     <span>{vendorsList?.find((v: any) => (v.id || v._id) === item.vendorId)?.vendorName || item.vendorId || 'Vendor'}{item.note ? ` [${item.note}]` : ''}:</span>
                                     <span className="font-semibold text-emerald-300">₹{Number(item.paidAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                   </div>
                                 ))}
                               </div>
                             </div>
                           )}
                         </div>
                       )}

                       {/* Cash In Hand summary bar */}
                       <div className="mt-4 flex items-center gap-3 bg-white/15 border border-white/20 rounded-xl px-5 py-3">
                         <span className="text-blue-200 text-xs flex-1">Cash In Hand = Meter + Lubricant − Credit − Swipe − Expenses − Cash to Bank + Opening + Recovery + Petty Cash + Cash Inflow − Outflows</span>
                         <span className="text-xl font-bold text-emerald-300">
                           ₹{(() => {
                             const transactionBalance = row.manual_transaction_balance !== null && row.manual_transaction_balance !== undefined
                               ? Number(row.manual_transaction_balance)
                               : (Number(row.meter_sale || 0) + Number(row.lubricant_sale || 0)
                                  - Number(row.credit_amount || 0) - Number(row.swipe_amount || 0)
                                  - Number(row.expenses || 0) - Number(row.cash_to_bank || 0)
                                  + Number(row.recovery_amount || 0));

                             const cashInHand = Number(row.opening_balance || 0)
                               + Number(row.petty_cash || 0) + Number(row.cash_inflow_amount || 0)
                               + transactionBalance
                               - Number(row.total_out_section_amount || 0);
                             return cashInHand.toLocaleString('en-IN', { minimumFractionDigits: 2 });
                           })()}
                         </span>
                       </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

